import React from 'react';
import PropTypes from 'prop-types';
import { Spinner } from '@pearsontechnology/aero-components';
import StyledLink from 'components/StyledLink';
import { PageActions, PageContent } from 'components/PageStyles';
import { featureKeys } from 'services/FeatureFlagService';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
  ScrollWrapper,
} from '../Grid/Table';
import AccountSummary from '../Grid/AccountSummary';
import CellValue from '../Grid/CellValue';
import enhanceWithFeatureFlagSubscription from 'hocs/enhanceWithFeatureFlagSubscription';

function getDocumentItemPerformance(
  performanceByDocumentItem,
  documentItemId,
  accountId,
) {
  const allDocumentItemPerformance = performanceByDocumentItem[documentItemId];
  if (!allDocumentItemPerformance) {
    return ' ';
  }

  const accountCompetencyRow = allDocumentItemPerformance.find((data) => data.account.id === accountId);
  if (!accountCompetencyRow || !accountCompetencyRow.learnerPerformanceByCompetency) {
    return undefined;
  }

  return accountCompetencyRow.learnerPerformanceByCompetency.value * 100;
}

function CompetencyGrid({
  isLoading,
  cohortId,
  deploymentId,
  enrollments,
  parentDocumentItem,
  childDocumentItems,
  performanceByDocumentItem,
  navigationActions,
  getFeatureFlagValue,
}) {
  const hasNoCompetencies = !parentDocumentItem && (!childDocumentItems || childDocumentItems.length === 0);
  const isNewDesignEnabled = getFeatureFlagValue(featureKeys.WORKSPACE_GRADES_NEW_DESIGN);

  return (
    <React.Fragment>
      <PageActions style={{ overflowX: 'auto', paddingTop: 4, flex: isNewDesignEnabled ? 'none' : undefined }}>
        {navigationActions}
      </PageActions>
      <PageContent flex100={isNewDesignEnabled}>
        { isLoading && <Spinner />}
        { !isLoading && hasNoCompetencies && (<em>No linked competencies found</em>)}
        { !isLoading && !hasNoCompetencies && (
          <ScrollWrapper>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell style={{ minWidth: 200, textAlign: 'left', paddingLeft: 16 }}>
                    { parentDocumentItem && (parentDocumentItem.abbreviatedStatement || parentDocumentItem.fullStatement)}
                  </TableHeaderCell>
                  { parentDocumentItem && (
                    <TableHeaderCell style={{ minWidth: 80 }}>
                      Total
                    </TableHeaderCell>
                  )}
                  {childDocumentItems.map((childDocumentItem) => (
                    <TableHeaderCell
                      key={childDocumentItem.id}
                      style={{ minWidth: 150 }}
                    >
                      <StyledLink to={`/classes/${cohortId}/grades/${deploymentId}/competency/${childDocumentItem.documentId}/${childDocumentItem.id}`}>
                        { childDocumentItem.abbreviatedStatement || childDocumentItem.fullStatement }
                      </StyledLink>
                    </TableHeaderCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {enrollments.map((enrollment) => (
                  <TableRow key={enrollment.account.id}>
                    <TableHeaderCell>
                      <AccountSummary account={enrollment.account.identity} />
                    </TableHeaderCell>
                    { parentDocumentItem && (
                      <TableCell>
                        <CellValue unit="%">
                          {getDocumentItemPerformance(
                            performanceByDocumentItem,
                            parentDocumentItem.id,
                            enrollment.account.id
                          )}
                        </CellValue>
                      </TableCell>
                    )}

                    {childDocumentItems.map((childDocumentItem) => (
                      <TableCell key={`${enrollment.account.id}-${childDocumentItem.id}`}>
                        <CellValue unit="%">
                          {getDocumentItemPerformance(
                            performanceByDocumentItem,
                            childDocumentItem.id,
                            enrollment.account.id
                          )}
                        </CellValue>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollWrapper>
        )}
      </PageContent>
    </React.Fragment>
  );
}

CompetencyGrid.propTypes = {
  isLoading: PropTypes.bool,
  parentDocumentItem: PropTypes.object,
  childDocumentItems: PropTypes.array,
  enrollments: PropTypes.array,
  cohortId: PropTypes.string,
  deploymentId: PropTypes.string,
  navigationActions: PropTypes.element,
  performanceByDocumentItem: PropTypes.object,
};

const flagNames = [featureKeys.WORKSPACE_GRADES_NEW_DESIGN];
export default enhanceWithFeatureFlagSubscription(CompetencyGrid, flagNames);
